import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMain } from 'src/views/hooks/useMain';
import BillTemplateRenderer from './BillTemplateRenderer';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import PageContainer from 'src/components/container/PageContainer';
import { Button } from '@mui/material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const LOGO_BOX_SIZE = 240; // resolution of the pre-rendered circular logo PNG

const BulkBillGenerator = () => {
    const [billsData, setBillsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();
    const { billIds, billType } = location.state || {};
    const componentRef = useRef();
    const billRefs = useRef([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

    // Caches the pre-rendered circular logo data URL per unique source URL,
    // so we only pay the (cache-busted) network + canvas cost once per unique
    // logo across the whole batch, instead of once per bill.
    const logoDataUrlCache = useRef({});

    const {
        fetchBillById,
        fetchPackageBillById,
        setShowMessage,
        branchData,
        activeBranchId,
        fetchClientByNumber,
        getFeedbackUrl,
        getBillUrl,
    } = useMain();

    useEffect(() => {
        const fetchAllBills = async () => {
            if (!billIds || billIds.length === 0) {
                setShowMessage({ message: 'No bills selected', messageType: 'error' });
                setLoading(false);
                return;
            }

            setLoading(true);
            const allBills = [];
            for (const billId of billIds) {
                try {
                    let res;
                    if (billType === 'package' || billType === 'membership') {
                        res = await fetchPackageBillById(billId, activeBranchId);
                    } else {
                        res = await fetchBillById(billId, `?branchId=${activeBranchId}`);
                        if (res.statusCode !== 200) {
                            res = await fetchPackageBillById(billId, activeBranchId);
                        }
                    }

                    if (res.statusCode === 200) {
                        const billData = res.data;
                        const clientRes = await fetchClientByNumber(billData.clientNumber, activeBranchId);
                        const feedbackUrlRes = await getFeedbackUrl(billId, `?branchId=${activeBranchId}`);
                        const billUrlRes = await getBillUrl(billId);

                        const formattedBill = {
                            ...billData,
                            branchData,
                            clientData: clientRes.data,
                            feedbackUrl: feedbackUrlRes.data || '',
                            billUrl: billUrlRes.data || '',
                            baseTotal: (billData.products?.reduce((total, product) => total + (product.price * product.quantity), 0) || 0) + (billData.services?.reduce((total, service) => total + (service.price * service.quantity), 0) || 0),
                            formattedDate: billData.dateOfBilling ? `${billData.dateOfBilling.split('-')[2]}-${billData.dateOfBilling.split('-')[1]}-${billData.dateOfBilling.split('-')[0]}` : '',
                            formattedTime: billData.timeOfBilling ? `${(parseInt(billData.timeOfBilling.split(':')[0]) % 12) || 12}:${billData.timeOfBilling.split(':')[1]} ${parseInt(billData.timeOfBilling.split(':')[0]) >= 12 ? 'PM' : 'AM'}` : '',
                        };
                        allBills.push(formattedBill);
                    }
                } catch (error) {
                    console.error(`Error fetching bill ${billId}:`, error);
                }
            }
            setBillsData(allBills);
            setLoading(false);
        };

        fetchAllBills();
    }, [billIds, activeBranchId]);

    // Preload the branch logo (and any other known static images) into the browser
    // cache as soon as we know branchData, so that by the time html2canvas runs
    // for each bill, the logo is already loaded and doesn't need a network round trip.
    useEffect(() => {
        if (branchData?.logoUrl) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = branchData.logoUrl;
        }
    }, [branchData?.logoUrl]);

    // Also preload each individual bill's logo URL (data?.branchDetails?.logo?.url),
    // since that's the field BillPdf actually renders — may differ from branchData.logoUrl.
    useEffect(() => {
        billsData.forEach((bill) => {
            const url = bill?.branchDetails?.logo?.url;
            if (url) {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.src = url;
            }
        });
    }, [billsData]);

    // Wait for every <img> inside the captured element to finish loading
    // (or fail) before we let html2canvas snapshot it.
    const waitForImagesToLoad = (element) => {
        const images = element.querySelectorAll('img');
        const promises = Array.from(images).map((img) => {
            if (!img.crossOrigin) {
                img.crossOrigin = 'anonymous';
            }
            if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
            return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve; // don't hang forever if an image fails
            });
        });
        return Promise.all(promises);
    };

    // Pre-renders a logo URL into a circular, white-backed, aspect-ratio-correct
    // PNG data URL using native Canvas 2D — completely independent of html2canvas,
    // CSS object-fit, or border-radius rendering quirks.
    //
    // IMPORTANT: the src is cache-busted with a unique query param on every call.
    // Without this, the browser can serve a stale, non-CORS-cleared cached copy
    // of the same image URL on repeat requests within the same batch, which
    // silently taints the canvas (drawImage succeeds but produces a blank/broken
    // result) for every bill after the first one that uses that logo URL.
    const buildCircularLogoDataUrl = (logoSrc, boxSize = LOGO_BOX_SIZE) => {
        return new Promise((resolve) => {
            if (!logoSrc) {
                resolve(null);
                return;
            }
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                if (img.width === 0 || img.height === 0) {
                    console.warn('Pre-render: logo has zero dimensions — likely CORS-blocked:', logoSrc);
                    resolve(null);
                    return;
                }
                try {
                    const off = document.createElement('canvas');
                    off.width = boxSize;
                    off.height = boxSize;
                    const ctx = off.getContext('2d');

                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(boxSize / 2, boxSize / 2, boxSize / 2, 0, Math.PI * 2);
                    ctx.closePath();
                    ctx.clip();

                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, boxSize, boxSize);

                    const imgRatio = img.width / img.height;
                    let drawW, drawH;
                    if (imgRatio > 1) {
                        drawW = boxSize;
                        drawH = boxSize / imgRatio;
                    } else {
                        drawH = boxSize;
                        drawW = boxSize * imgRatio;
                    }
                    const drawX = (boxSize - drawW) / 2;
                    const drawY = (boxSize - drawH) / 2;

                    ctx.drawImage(img, drawX, drawY, drawW, drawH);
                    ctx.restore();

                    resolve(off.toDataURL('image/png'));
                } catch (err) {
                    // Most commonly a SecurityError from a CORS-tainted canvas
                    console.error('Failed to build circular logo (likely CORS/cache tainted):', err);
                    resolve(null);
                }
            };
            img.onerror = () => {
                console.error('Logo failed to load for pre-render:', logoSrc);
                resolve(null);
            };

            // Cache-bust so every fetch is guaranteed fresh and CORS-checked.
            const separator = logoSrc.includes('?') ? '&' : '?';
            img.src = `${logoSrc}${separator}_cb=${Date.now()}_${Math.random().toString(36).slice(2)}`;
        });
    };

    // Returns a cached circular logo data URL for this source if we've already
    // built one this session, otherwise builds it once and caches the result.
    const getCircularLogoDataUrl = async (logoSrc) => {
        if (!logoSrc) return null;
        if (logoDataUrlCache.current[logoSrc]) {
            return logoDataUrlCache.current[logoSrc];
        }
        const dataUrl = await buildCircularLogoDataUrl(logoSrc, LOGO_BOX_SIZE);
        if (dataUrl) {
            logoDataUrlCache.current[logoSrc] = dataUrl;
        }
        return dataUrl;
    };

    const handleDownloadAllSeparately = async () => {
        if (!billsData || billsData.length === 0) return;

        setIsDownloading(true);
        setDownloadProgress({ current: 0, total: billsData.length });

        for (let i = 0; i < billsData.length; i++) {
            const element = billRefs.current[i];
            if (!element) continue;

            // Hide elements marked "no-print" before capture
            const hiddenEls = element.querySelectorAll('.no-print, .bill-actions, button');
            const prevDisplay = [];
            hiddenEls.forEach((el) => {
                prevDisplay.push(el.style.display);
                el.style.display = 'none';
            });

            const logoImg = element.querySelector('.standard-branch-logo');
            const logoSrc = logoImg?.src || null;
            console.log(`[Bill ${i}] logoSrc:`, logoSrc);

            try {
                // 1. Make sure every image (logo included) has actually finished loading
                await waitForImagesToLoad(element);

                // 2. Give the browser two extra paint cycles to settle layout/images
                await new Promise((resolve) =>
                    requestAnimationFrame(() => requestAnimationFrame(resolve))
                );

                // 3. Pre-build (or reuse from cache) the final circular logo PNG
                // BEFORE capture — onclone just swaps it in as a finished image,
                // so there's no dependency on html2canvas correctly interpreting
                // object-fit / border-radius / background-image at capture time.
                const circularLogoDataUrl = await getCircularLogoDataUrl(logoSrc);
                console.log(`[Bill ${i}] circularLogoDataUrl:`, circularLogoDataUrl ? 'READY' : 'FAILED (null)');

                // 4. Capture
                // NOTE: foreignObjectRendering removed — combined with cross-origin
                // images it produces a tainted/blank SVG, which is why the whole
                // PDF content went blank previously. windowWidth/windowHeight
                // removed too, since forcing a fake viewport caused an earlier
                // logo scaling bug.
                const scale = 2; // keep this in sync with the math below
                const canvas = await html2canvas(element, {
                    scale,
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                    imageSmoothingEnabled: true,
                    onclone: (clonedDoc) => {
                        // IMPORTANT: this callback returns a Promise, and
                        // html2canvas waits for it before snapshotting.
                        // Assigning `.src` alone does NOT guarantee the new
                        // image has actually loaded/painted by the time
                        // html2canvas captures the clone — that race is what
                        // caused only the first bill in a batch to render the
                        // logo correctly while later ones captured a blank or
                        // stale frame. Explicitly waiting for onload closes
                        // that race for every bill, regardless of batch size
                        // or how fast/slow earlier bills processed.
                        return new Promise((resolveClone) => {
                            const clonedLogo = clonedDoc.querySelector('.standard-branch-logo');
                            if (!clonedLogo) {
                                resolveClone();
                                return;
                            }

                            const parent = clonedLogo.parentElement;
                            if (parent) {
                                parent.style.width = '120px';
                                parent.style.height = '120px';
                                parent.style.overflow = 'visible';
                            }

                            if (!circularLogoDataUrl) {
                                // Pre-render failed for this bill — hide rather
                                // than show a broken/stretched logo.
                                clonedLogo.style.display = 'none';
                                resolveClone();
                                return;
                            }

                            clonedLogo.style.width = '120px';
                            clonedLogo.style.height = '120px';
                            clonedLogo.style.objectFit = 'contain';
                            clonedLogo.style.borderRadius = '0'; // already circular in the image itself

                            let settled = false;
                            const settle = () => {
                                if (settled) return;
                                settled = true;
                                resolveClone();
                            };

                            clonedLogo.onload = settle;
                            clonedLogo.onerror = settle;
                            clonedLogo.src = circularLogoDataUrl;

                            // Data URLs are usually synchronous/instant, but as a
                            // safety net in case onload doesn't fire in time in
                            // some browsers, resolve after a short delay regardless.
                            setTimeout(settle, 150);
                        });
                    },
                });

                const imgData = canvas.toDataURL('image/png');

                // canvas.width/height already include the `scale` factor baked in
                // by html2canvas, so convert straight to mm — do NOT divide by scale again.
                const pxToMm = (px) => px * 0.264583;
                const imgWidthMm = pxToMm(canvas.width);
                const imgHeightMm = pxToMm(canvas.height);

                const pdf = new jsPDF({
                    orientation: imgWidthMm > imgHeightMm ? 'landscape' : 'portrait',
                    unit: 'mm',
                    format: [imgWidthMm, imgHeightMm],
                });

                pdf.addImage(imgData, 'PNG', 0, 0, imgWidthMm, imgHeightMm, undefined, 'FAST');

                const bill = billsData[i];
                const fileName = `Bill_${bill.clientName || 'client'}_${bill.formattedDate || i + 1}_${Date.now()}.pdf`
                    .replace(/[^a-zA-Z0-9_\-\.]/g, '_'); // sanitize filename

                pdf.save(fileName);

                setDownloadProgress({ current: i + 1, total: billsData.length });

                // small delay so browser doesn't block multiple rapid downloads
                await new Promise((resolve) => setTimeout(resolve, 400));
            } catch (error) {
                console.error(`Error generating PDF for bill ${i}:`, error);
            } finally {
                // Restore hidden elements
                hiddenEls.forEach((el, idx) => {
                    el.style.display = prevDisplay[idx];
                });
            }
        }

        setIsDownloading(false);
    };

    return (
        <PageContainer title="Bulk Bill Invoice">
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between' }}>
                <Button variant="outlined" color="primary" onClick={() => navigate('/billings')}>
                    Back to All Billings
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDownloadAllSeparately}
                    disabled={isDownloading || billsData.length === 0}
                >
                    {isDownloading
                        ? `Downloading ${downloadProgress.current}/${downloadProgress.total}...`
                        : 'Download All (Separate PDFs)'}
                </Button>
            </div>
            {loading ? (
                <ShowLoader />
            ) : billsData.length === 0 ? (
                <div>No bill records found</div>
            ) : (
                <div ref={componentRef}>
                    {billsData.map((bill, index) => (
                        <div
                            key={index}
                            className="page-break"
                            ref={(el) => (billRefs.current[index] = el)}
                            style={{
                                width: '800px',
                                margin: '0 auto',
                                backgroundColor: '#ffffff',
                            }}
                        >
                            <BillTemplateRenderer
                                selectedTemplate={branchData?.selectedBillTemplate !== undefined ? branchData.selectedBillTemplate : 1}
                                billData={bill}
                                branchData={branchData}
                            />
                        </div>
                    ))}
                </div>
            )}
        </PageContainer>
    );
};

export default BulkBillGenerator;